import { FunctionComponent } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';

export type StandingsEmptyVariant = 'select-stage' | 'knockout' | 'no-standings';

type StandingsEmptyStateProps = {
  variant: StandingsEmptyVariant;
};

const PREFIX = 'pages.application_championship_stage_standings.empty_state';

type Tone = {
  bg: string;
  border: string;
  iconColor: string;
  iconBg: string;
  titleColor: string;
  textColor: string;
};

const TONES: Record<StandingsEmptyVariant, Tone> = {
  'select-stage': {
    bg: 'gray.50',
    border: 'gray.200',
    iconColor: 'gray.500',
    iconBg: 'gray.100',
    titleColor: 'gray.700',
    textColor: 'gray.600',
  },
  knockout: {
    bg: 'orange.50',
    border: 'orange.200',
    iconColor: 'orange.500',
    iconBg: 'orange.100',
    titleColor: 'orange.700',
    textColor: 'orange.700',
  },
  'no-standings': {
    bg: 'blue.50',
    border: 'blue.200',
    iconColor: 'blue.500',
    iconBg: 'blue.100',
    titleColor: 'blue.700',
    textColor: 'blue.700',
  },
};

const ICONS: Record<StandingsEmptyVariant, string> = {
  'select-stage': 'filter',
  knockout: 'git-branch',
  'no-standings': 'users',
};

export const StandingsEmptyState: FunctionComponent<StandingsEmptyStateProps> = ({ variant }) => {
  const { t } = useTranslation();
  const tone = TONES[variant];
  const icon = ICONS[variant];

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap={3}
      bg={tone.bg}
      border="1px solid"
      borderColor={tone.border}
      borderRadius="md"
      py={10}
      px={6}
      textAlign="center"
    >
      <Box
        w="48px"
        h="48px"
        borderRadius="full"
        bg={tone.iconBg}
        color={tone.iconColor}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <ChakraFeatherIcon name={icon} boxSize="22px" />
      </Box>
      <Text fontWeight="semibold" color={tone.titleColor}>
        {t(`${PREFIX}.${variant}.title`)}
      </Text>
      <Text fontSize="sm" color={tone.textColor} maxW="540px">
        {t(`${PREFIX}.${variant}.description`)}
      </Text>
    </Flex>
  );
};
